(ns io.daskul.webapp.widgets.record.pinned-facts
  "The pinned-facts strip (F-060 zone 3): a row of eyebrow + value cells under
   the tabs, carrying the handful of read-only facts you want visible without
   scrolling.

   This is what the right sidebar becomes. That rail had accumulated four
   unrelated jobs — transitions, audit metadata, read-only sections and the
   activity widget — and nothing in it was emphasised, because everything was
   a card of equal weight. A card is a heavy container for one date.

   A fact is READ-ONLY here by construction. `:pinned` is a statement about
   the fields, not about layout, which is the impurity `:placement :sidebar`
   carried and F-056 flagged and accepted.

   Empty facts do not render. A section whose fields all resolve to nothing
   used to leave an empty card on the page — visible on the Lead detail, where
   Active Trial renders nothing for a lead that has no trial."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $]]))

(defn- format-datetime
  "Format ISO date string as full date+time (e.g., 'Feb 18, 2026 3:13 PM')."
  [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)]
        (.toLocaleString date "en-US" #js {:month "short" :day "numeric" :year "numeric"
                                           :hour "numeric" :minute "2-digit"}))
      (catch :default _
        iso-string))))

(defn- format-relative
  "Format ISO date string as relative time (e.g., 'just now', '2 hours ago', '3 days ago').
   Falls back to short date for dates older than 30 days."
  [iso-string]
  (when iso-string
    (try
      (let [date (js/Date. iso-string)
            now (js/Date.)
            diff-ms (- (.getTime now) (.getTime date))
            diff-secs (/ diff-ms 1000)
            diff-mins (/ diff-secs 60)
            diff-hours (/ diff-mins 60)
            diff-days (/ diff-hours 24)]
        (cond
          (< diff-secs 60) "just now"
          (< diff-mins 60) (let [m (js/Math.floor diff-mins)]
                             (str m (if (= m 1) " minute ago" " minutes ago")))
          (< diff-hours 24) (let [h (js/Math.floor diff-hours)]
                              (str h (if (= h 1) " hour ago" " hours ago")))
          (< diff-days 30) (let [d (js/Math.floor diff-days)]
                             (str d (if (= d 1) " day ago" " days ago")))
          :else (.toLocaleDateString date "en-US" #js {:month "short" :day "numeric" :year "numeric"})))
      (catch :default _
        iso-string))))

(def max-facts
  "A framework constant, not a convention (F-060). Past four the strip stops
   being scannable and becomes a second form — at which point the extras
   belong in the form itself, or the aggregate is trying to say too much
   above the fold."
  4)

(defn- humanize
  "`follow-up-date` -> `Follow-up Date`. Only reached when neither the pinned
   spec nor the after-read config names the field."
  [field-key]
  (->> (str/split (name field-key) #"-")
       (map str/capitalize)
       (str/join " ")))

(defn facts-from-config
  "Build the strip's facts from the `:ui :detail :sidebar` spec — the same
   declaration the right rail read, which is why retiring the rail needs no
   product change.

   Pure, and deliberately does NOT render: it returns the raw value plus its
   display config so the caller decides how to draw it, which keeps the cap
   and blank rules testable without a DOM.

   A field is skipped when nothing describes it (no after-read config, no
   :display, no :label) — that spec is not a fact, it is a typo."
  [{:keys [sidebar-fields after-read-fields record]}]
  (->> sidebar-fields
       (keep (fn [sf]
               (let [field-key (keyword (:key sf))
                     ar-config (get after-read-fields field-key)
                     described? (or ar-config (:display sf) (:label sf))]
                 (when described?
                   {:key field-key
                    ;; The FIELD, kept alongside the display key. A section's
                    ;; fact is keyed by the section id, so an alert naming the
                    ;; field it tested cannot find it by key alone — matching
                    ;; on this is what lets a condition tone its own fact.
                    :field field-key
                    :label (or (:label sf) (:label ar-config) (humanize field-key))
                    :value (get record field-key)
                    :display-config (or (:display sf) (:display ar-config))}))))
       vec))

(defn facts-from-sections
  "Facts from the `:readonly` sidebar form sections — F-060's `:pinned`.

   A section declaring `:placement :sidebar :readonly true` is a statement
   about its FIELDS (they cannot be edited here), which the old key confused
   with a statement about layout. Reading the readonly flag rather than the
   placement is what lets the rail retire without touching a product.

   The section title labels the fact when it has a single field — `Follow-up`
   over the date — and each field labels itself when there are several, since
   one title cannot name two values.

   Sections carrying a `:widget` are skipped: an Activity log is not a fact."
  [{:keys [sections field-configs record]}]
  (->> sections
       (remove :widget)
       (filter :readonly)
       (mapcat (fn [{:keys [id title fields]}]
                 (let [single? (= 1 (count fields))]
                   (map (fn [field-key]
                          {:key (if single? (or id field-key) field-key)
                           :field field-key
                           :label (if single?
                                    (or title (humanize field-key))
                                    (or (get-in field-configs [field-key :label])
                                        (humanize field-key)))
                           :value (get record field-key)
                           :display-config (get-in field-configs [field-key :display])})
                        fields))))
       vec))

(def tone->colors
  "Severity vocabulary, shared with the banners (F-059) and deliberately
   sharing no word with the state-chip tones (`attention active blocked
   settled closed`). If the two ever share a word someone will assume they are
   the same axis."
  {:critical {:bg "var(--critical-bg)" :fg "var(--critical-fg)"
              :border "var(--critical-border)"}
   :warning  {:bg "var(--warning-bg)"  :fg "var(--warning-fg)"
              :border "var(--warning-border)"}})

(defn tone-colors
  "Colours for a fact's tone, or nil when it has none. An unrecognised tone
   degrades to untoned rather than throwing — a typo in a definition should
   not blank the strip."
  [tone]
  (get tone->colors (some-> tone keyword)))

(defn- unrenderable?
  "True when a value cannot be drawn as a fact.

   Blank is the obvious case. The other is STRUCTURED: a ClojureScript
   collection is never a valid React child, so rendering one raw throws and
   takes the whole page with it. `:active-trial` is exactly this — its
   after-read value is a map, and every lead checked by hand had no trial, so
   it was always nil and the crash only surfaced in the e2e critical path.

   A collection needs a `:display` config to become a fact. Without one there
   is nothing to render, so it is dropped rather than guessed at.

   Note `coll?` is false for a string (so dates and labels are fine) and for a
   React element returned by render-display-value (a JS object), so a
   configured fact still renders."
  [v]
  (or (nil? v)
      (and (string? v) (= "" (.trim v)))
      (coll? v)))

(defn visible-facts
  "Drop facts with no value, then cap. Pure, so the cap and the emptiness rule
   are testable without rendering.

   Returns at most `max-facts`; anything beyond that is dropped rather than
   wrapped, because a strip that reflows to two rows is no longer a strip."
  [facts]
  (->> facts
       (remove #(unrenderable? (:value %)))
       (take max-facts)
       vec))

(def link-glyph
  "Unicode, not the tabler set — requiring `webapp.ui` here pulls in icons the
   :node-test build cannot resolve, which takes down the whole cljs suite and
   not just this namespace's tests. `record.alerts` uses unicode for its
   severity glyphs for the same reason."
  {:phone "☎" :message "💬" :mail "✉"})

(defui ^:private fact-link
  "Where the work is, under the fact it belongs to.

   CLICKABLE ONLY WHEN FOLLOWING IT IS PREDICTABLE. \"Open conversation\"
   routes; a phone number does not dial. Showing the number and letting
   someone press it themselves is the honest rendering — the alternative
   silently placed a call from a control whose label was a date."
  [{:keys [link on-link]}]
  (let [{:keys [icon label]} link
        glyph (get link-glyph (some-> icon keyword))
        ;; --body-size, not --helper-size. This is the thing you are meant to
        ;; act on; at 12px in --text-muted it read as a footnote beside a
        ;; bold amber pill, and the number you are supposed to dial was the
        ;; least legible text in the cell.
        base {:display "inline-flex" :alignItems "center" :gap "6px"
              :marginTop "6px"
              :fontSize "var(--body-size)"
              :fontWeight 600
              :fontFamily "var(--font-body)"
              :whiteSpace "nowrap"}]
    (if on-link
      ($ :button {:type "button"
                  :onClick on-link
                  :style (assoc base
                                :border "none" :background "none" :padding 0
                                :cursor "pointer"
                                :color "var(--action)")}
        (when glyph ($ :span {:style {:fontSize "15px" :opacity 0.75}} glyph))
        ($ :span {:style {:textDecoration "underline"}} label))
      ;; --text, not --text-muted: a phone number nobody can read is not a
      ;; contact affordance.
      ($ :div {:style (assoc base :color "var(--text)")}
        (when glyph ($ :span {:style {:fontSize "15px" :opacity 0.55}} glyph))
        ($ :span nil label)))))

(defui pinned-facts
  "Props: facts — [{:key :label :value}], where :value is already rendered.

   A bordered CARD in the body, not a bare row in the header band. Sitting in
   the band it was a lonely label floating in white with a rule under it, and
   at one fact it read as a rendering failure. The reference draws it as the
   first card of the Details tab, which is also where it belongs
   semantically: these are facts about the record, not part of its identity.

   Cells share the row evenly ONLY when there are several. A single fact that
   stretches to 1400px is the same mistake in a different container, so it
   takes its natural width instead.

   Renders nothing at all when no fact has a value, rather than an empty
   container — the failure this replaces."
  [{:keys [facts]}]
  (let [shown (visible-facts facts)
        many? (> (count shown) 1)]
    (when (seq shown)
      ($ :div {:data-testid "pinned-facts"
               :style {:display "flex"
                       :alignItems "stretch"
                       :background "var(--surface-card)"
                       :border "1px solid var(--border)"
                       :borderRadius "var(--mantine-radius-md)"
                       :padding "13px var(--card-pad-x, 18px)"
                       ;; Matches the gap every other card in this column
                       ;; carries — measured 16px between Trial Bookings and
                       ;; the form. Without it the strip sat flush against the
                       ;; next card, the only seam on the page with no gap.
                       :marginBottom "var(--mantine-spacing-md)"}}
        (->> shown
             (map-indexed
              (fn [i {:keys [key label value tone on-action action-label
                             link on-link]}]
                ($ :div {:key (str key)
                         :style (cond-> {:minWidth 0
                                         :paddingRight "20px"
                                         :paddingLeft (if (zero? i) "0" "20px")
                                         ;; The divider is a border on the
                                         ;; cell, not a spacer element — one
                                         ;; less node per fact, and it cannot
                                         ;; drift out of sync with the gap.
                                         :borderLeft (when-not (zero? i)
                                                       "1px solid var(--border-subtle)")}
                                  many? (assoc :flex 1))}
                  ($ :div {:style {:fontFamily "var(--font-heading)"
                                   :fontSize "var(--eyebrow-size)"
                                   :fontWeight "var(--eyebrow-weight)"
                                   :letterSpacing "var(--eyebrow-tracking)"
                                   :textTransform "uppercase"
                                   :color "var(--text-faint)"
                                   :marginBottom "6px"}}
                    label)
                  ;; TONE IS THE AFFORDANCE for the CONDITION: the banner
                  ;; explains it once and scrolls away, this stays. Amber
                  ;; never fills — a tint with a border, never a solid.
                  ;;
                  ;; What to DO about it renders separately, below, because a
                  ;; pill reading "2026-07-28 · overdue" that placed a phone
                  ;; call was an action nobody could predict from its label.
                  (if-let [{:keys [bg fg border]} (tone-colors tone)]
                    ;; Two explicit branches rather than `($ (if …) …)`: uix's
                    ;; `$` is a macro, and a computed tag is not something to
                    ;; rely on it accepting.
                    (let [pill {:display "inline-flex" :alignItems "center"
                                :gap "6px"
                                :fontSize "var(--body-size)" :fontWeight 700
                                :borderRadius "var(--mantine-radius-xl)"
                                :padding "4px 11px" :whiteSpace "nowrap"
                                :color fg :background bg
                                :border (str "1px solid " border)}
                          chevron ($ :span {:style {:fontSize "11px"
                                                    :opacity 0.75}} "›")]
                      (if on-action
                        ($ :button {:type "button"
                                    :onClick on-action
                                    :title action-label
                                    :style (assoc pill :cursor "pointer")}
                          value chevron)
                        ($ :div {:style (assoc pill :cursor "default")}
                          value)))
                    ($ :div {:style {:fontSize "var(--body-size)"
                                     :fontWeight 600
                                     :color "var(--text)"
                                     :overflow "hidden"
                                     :textOverflow "ellipsis"}}
                      value))
                  (when link
                    ($ fact-link {:link link :on-link on-link})))))
             (into []))))))

(defui record-meta
  "Created / updated, as one quiet line at the foot of Details.

   Audit metadata is system-generated, never editable, and almost never acted
   on — so it does not belong under the record title, which is the most
   valuable spot on the page, and it does not deserve a card. Written in words
   rather than glyphs: a glyph you must hover to decode is not quieter than a
   word, just worse.

   Domain dates (invoice due, enrolment start) are a different thing entirely —
   real, editable, business-meaningful — and belong in the strip above."
  [{:keys [created-at updated-at]}]
  (let [created (format-relative created-at)
        updated (format-relative updated-at)]
   (when (or created updated)
    ($ :div {:data-testid "record-meta"
             :style {:fontSize "var(--helper-size)"
                     :color "var(--text-faint)"
                     :marginTop "var(--section-pad)"}}
      (->> [(when created ($ :span {:key "c" :title (format-datetime created-at)}
                             (str "Created " created)))
            (when updated ($ :span {:key "u" :title (format-datetime updated-at)}
                             (str "Updated " updated)))]
           (remove nil?)
           (interpose ($ :span {:key "sep"} " · "))))))) 
